import React, {
  useState, useRef, useEffect, useLayoutEffect, useCallback,
} from 'react';
import {createPortal} from 'react-dom';
import classnames from 'classnames';

export type OptionsSelectMode = 'single' | 'multi' | 'single-apply';

export type OptionsSelectPopoverItem = {
  id: string;
  label: string;
  tooltip?: string;
};

export type OptionsSelectPopoverAction = {
  label: string;
  onClick: () => void;
};

type OptionsSelectPopoverProps = {
  id: string;
  isOpen: boolean;
  coords: {top: number; left: number};
  width?: number;
  mode: OptionsSelectMode;
  options: OptionsSelectPopoverItem[];
  selectedIds: string[];
  onSelect: (id: string) => void;
  title?: string;
  primaryAction?: OptionsSelectPopoverAction;
  secondaryAction?: OptionsSelectPopoverAction;
};

/**
 * Portal-rendered popover used by SymbolPicker for its "Exchanges" filter.
 * Supports single-select, multi-select checkbox lists, and an optional footer
 * with primary/secondary actions (e.g. "All" / "None"). A custom scrollbar is
 * shown only when the list overflows the fixed max height.
 */
export const OptionsSelectPopover: React.FC<OptionsSelectPopoverProps> = ({
  id,
  isOpen,
  coords,
  width = 176,
  mode,
  options,
  selectedIds,
  onSelect,
  title,
  primaryAction,
  secondaryAction,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const [thumbHeight, setThumbHeight] = useState(0);
  const [thumbTop, setThumbTop] = useState(0);
  const [needsScroll, setNeedsScroll] = useState(false);
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startThumbTop = useRef(0);

  const [measuredRowHeight, setMeasuredRowHeight] = useState<number | undefined>(undefined);

  // Keep the custom scrollbar thumb geometry in sync with whatever the browser
  // is currently showing - called on open, scroll, and resize observer events.
  const updateScrollIndicator = useCallback(() => {
    if (!listRef.current) {
      return;
    }

    const {scrollTop, scrollHeight, clientHeight} = listRef.current;

    if (scrollHeight <= clientHeight) {
      setNeedsScroll(false);
      setThumbHeight(0);
      setThumbTop(0);
      return;
    }

    setNeedsScroll(true);

    if (!trackRef.current) {
      return;
    }

    const trackHeight = trackRef.current.clientHeight;
    const height = Math.max((clientHeight / scrollHeight) * trackHeight, 20);
    const top = (scrollTop / (scrollHeight - clientHeight)) * (trackHeight - height);
    setThumbHeight(height);
    setThumbTop(top);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setNeedsScroll(false);
      setThumbHeight(0);
      setThumbTop(0);
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    updateScrollIndicator();
  }, [isOpen, options, mode, measuredRowHeight, updateScrollIndicator]);

  useEffect(() => {
    if (!isOpen || !listRef.current) {
      return;
    }

    const element = listRef.current;
    const observer = new ResizeObserver(() => {
      updateScrollIndicator();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [isOpen, updateScrollIndicator]);

  // Measure the height of the first rendered row so we can set the list's
  // fixed height to a multiple of the row height (feels more deterministic).
  useEffect(() => {
    if (!isOpen || !listRef.current) {
      return;
    }

    const firstRow = listRef.current.firstElementChild;
    if (!(firstRow instanceof HTMLElement)) {
      return;
    }

    const nextRowHeight = firstRow.getBoundingClientRect().height;
    if (nextRowHeight > 0) {
      setMeasuredRowHeight(nextRowHeight);
    }
  }, [isOpen, options, mode]);

  const handleThumbMouseMove = useCallback((event: MouseEvent) => {
    if (!isDragging.current || !listRef.current || !trackRef.current) {
      return;
    }

    const deltaY = event.clientY - startY.current;
    const trackHeight = trackRef.current.clientHeight;
    const maxThumbTop = trackHeight - thumbHeight;

    if (maxThumbTop <= 0) {
      return;
    }

    const newThumbTop = Math.min(Math.max(startThumbTop.current + deltaY, 0), maxThumbTop);
    const scrollRatio = newThumbTop / maxThumbTop;
    const {scrollHeight, clientHeight} = listRef.current;
    listRef.current.scrollTop = scrollRatio * (scrollHeight - clientHeight);
    setThumbTop(newThumbTop);
  }, [thumbHeight]);

  const handleThumbMouseUp = useCallback(() => {
    isDragging.current = false;
    document.removeEventListener('mousemove', handleThumbMouseMove);
    document.removeEventListener('mouseup', handleThumbMouseUp);
  }, [handleThumbMouseMove]);

  const handleThumbMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    isDragging.current = true;
    startY.current = event.clientY;
    startThumbTop.current = thumbTop;
    document.addEventListener('mousemove', handleThumbMouseMove);
    document.addEventListener('mouseup', handleThumbMouseUp);
  };

  useEffect(() => () => {
    document.removeEventListener('mousemove', handleThumbMouseMove);
    document.removeEventListener('mouseup', handleThumbMouseUp);
  }, [handleThumbMouseMove, handleThumbMouseUp]);

  if (!isOpen) {
    return null;
  }

  const showFooter = Boolean(primaryAction ?? secondaryAction);
  const isMultiMode = mode === 'multi';
  const fallbackRowHeight = isMultiMode ? 24 : 28;
  const rowHeight = measuredRowHeight ?? fallbackRowHeight;
  const maxVisibleItems = 10;
  const visibleItemCount = Math.min(Math.max(options.length, 1), maxVisibleItems);
  const listHeight = visibleItemCount * rowHeight;

  return createPortal(
    <div
      id={id}
      className='sp-root sp-popover'
      style={{
        top: coords.top,
        left: coords.left,
        width: `${width}px`,
        display: 'block',
      }}
    >
      {title && (
        <div className='sp-popover-title'>{title}</div>
      )}
      <div className='sp-popover-body' style={{height: `${listHeight}px`}}>
        <div
          ref={listRef}
          className='sp-popover-list'
          onScroll={updateScrollIndicator}
        >
          {options.map(option => {
            const isSelected = selectedIds.includes(option.id);

            if (isMultiMode) {
              return (
                <label
                  key={option.id}
                  className='sp-popover-row'
                >
                  <input
                    type='checkbox'
                    checked={isSelected}
                    onChange={() => {
                      onSelect(option.id);
                    }}
                  />
                  <span>{option.label}</span>
                </label>
              );
            }

            return (
              <button
                key={option.id}
                type='button'
                className='sp-popover-row'
                onClick={() => {
                  onSelect(option.id);
                }}
              >
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
        <div
          ref={trackRef}
          className={classnames('sp-popover-track', {'sp-popover-track--visible': needsScroll})}
        >
          {needsScroll && thumbHeight > 0 && (
            <div
              className='sp-popover-thumb'
              style={{height: `${thumbHeight}px`, top: `${thumbTop}px`}}
              onMouseDown={handleThumbMouseDown}
            />
          )}
        </div>
      </div>
      {showFooter && (
        <div className='sp-popover-footer'>
          <div>
            {primaryAction && (
              <button
                type='button'
                className='sp-popover-action sp-popover-action--primary'
                onClick={primaryAction.onClick}
              >
                {primaryAction.label}
              </button>
            )}
          </div>
          <div>
            {secondaryAction && (
              <button
                type='button'
                className='sp-popover-action sp-popover-action--secondary'
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </button>
            )}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};
